import React from 'react';
import type { CalculatedValues } from './types';
import { labelClassName } from './types';
import { formatDecimalString } from '@/utils/decimal-display';

interface TradeSummarySectionProps {
  securityCurrency: string;
  effectiveSettlementCurrency: string;
  isCrossCurrency: boolean;
  calculatedValues: CalculatedValues;
  /** LR-626: Closing Settlement is GMRA-only. Passed in rather than derived —
   *  this section receives no trade type. */
  showClosingSettlement: boolean;
}

// Currency-aware, crash-safe money formatter (LR-459, hardened round 2).
// Delegates to the app-wide guarded `formatDecimalString` (its currency symbol
// goes through a try/catch'd Intl, falling back to "<CCY> 1,234.00" on a bad ISO
// code) instead of a raw `new Intl.NumberFormat({style:'currency'})`, which threw
// a RangeError on any non-ISO currency (e.g. the 'N/A' an inventory feed can
// emit) and — with no ErrorBoundary over the modal — white-screened the whole
// app. Same symbol style as the blotter / single-trade view; -0 normalised.
const money = (value: number, currency: string): string =>
  formatDecimalString(value, { currency, dp: 2 });

interface SummaryCell {
  key: string;
  label: string;
  value: string;
  valueClassName?: string;
}

const MetricCell: React.FC<{ cell: SummaryCell }> = ({ cell }) => (
  <div>
    <label className={labelClassName}>{cell.label}</label>
    <div className={`text-sm font-medium ${cell.valueClassName ?? 'text-gray-900'}`}>
      {cell.value}
    </div>
  </div>
);

// LR-537: full-width divider opening a currency block (cross-ccy only) —
// currency pill, its role in the trade, hairline. Styled after the
// "Settles in {ccy}" badge on the trade-detail Terms tab.
const CurrencyBlockHeader: React.FC<{ currency: string; role: 'Settlement' | 'Security' }> = ({
  currency,
  role,
}) => (
  <div className="col-span-full flex items-center gap-2">
    <span className="rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-gray-700">
      {currency}
    </span>
    <span className="text-[11px] uppercase tracking-wide text-gray-500">{role} Currency</span>
    <span className="flex-1 border-t border-gray-200" aria-hidden="true" />
  </div>
);

export const TradeSummarySection: React.FC<TradeSummarySectionProps> = ({
  securityCurrency,
  effectiveSettlementCurrency,
  isCrossCurrency,
  calculatedValues,
  showClosingSettlement,
}) => {
  const pct = (value: number): string =>
    `${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  const signColor = (value: number): string => (value < 0 ? 'text-red-600' : 'text-green-600');

  // Currency-neutral metrics (FX Rate describes the pair — it belongs to
  // neither currency block and renders once, cross-ccy only).
  const dailyRateCell: SummaryCell = {
    key: 'daily-rate',
    label: 'Daily Rate (%)',
    value: pct(calculatedValues.dailyRate * 100),
  };
  const marginCell: SummaryCell = {
    key: 'margin',
    label: 'Margin (%)',
    value: pct(calculatedValues.marginPercent),
  };
  const fxRateCell: SummaryCell | null =
    isCrossCurrency && calculatedValues.fxRate
      ? {
          key: 'fx-rate',
          label: 'FX Rate',
          value: `1 ${securityCurrency} = ${calculatedValues.fxRate.toFixed(6)} ${effectiveSettlementCurrency}`,
        }
      : null;

  // Security-ccy cells. Collateral stays in the original (security) currency —
  // unconverted (feedback p.4). Closing Settlement keeps its pre-grouping
  // guard (undefined while the form is incomplete: trade type not chosen,
  // GMRA without settlement date, TERM without maturity date).
  const contractValueSecurity: SummaryCell = {
    key: 'cv-sec',
    label: `Contract Value (${securityCurrency})`,
    value: money(calculatedValues.contractValue, securityCurrency),
  };
  const collateralRequired: SummaryCell = {
    key: 'collateral',
    label: `Collateral Required (${securityCurrency})`,
    value: money(calculatedValues.collateralRequiredSecurity, securityCurrency),
  };
  const expectedDailyReturnSecurity: SummaryCell = {
    key: 'edr-sec',
    label: `Expected Daily Return (${securityCurrency})`,
    value: money(calculatedValues.expectedDailyReturnSecurity, securityCurrency),
    valueClassName: signColor(calculatedValues.expectedDailyReturnSecurity),
  };
  const marginPriceSecurity: SummaryCell = {
    key: 'mp-sec',
    label: `Margin Price (${securityCurrency})`,
    value: money(calculatedValues.marginPrice, securityCurrency),
  };
  const netSettlementSecurity: SummaryCell = {
    key: 'ns-sec',
    label: `Net Settlement (${securityCurrency})`,
    value: money(calculatedValues.netSettlementSecurity, securityCurrency),
    valueClassName: 'text-blue-600',
  };
  const closingSettlementSecurity: SummaryCell | null =
    showClosingSettlement && calculatedValues.closingSettlement !== undefined
      ? {
          key: 'cs-sec',
          label: `Closing Settlement (${securityCurrency})`,
          value: money(calculatedValues.closingSettlement, securityCurrency),
          valueClassName: 'text-purple-600',
        }
      : null;

  if (!isCrossCurrency) {
    // Single-currency trades keep the pre-LR-537 flat order untouched.
    const flatOrder = [
      contractValueSecurity,
      collateralRequired,
      dailyRateCell,
      expectedDailyReturnSecurity,
      marginCell,
      marginPriceSecurity,
      netSettlementSecurity,
      closingSettlementSecurity,
    ].filter((cell): cell is SummaryCell => cell !== null);

    return (
      <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        {flatOrder.map((cell) => (
          <MetricCell key={cell.key} cell={cell} />
        ))}
      </div>
    );
  }

  // LR-537 (feedback p.57): the cross-ccy summary is grouped by currency —
  // neutral metrics first, then the settlement-ccy block, then the
  // security-ccy block, each opened by a full-width sub-header. The
  // settlement block leads, keeping the LR-474 settlement-first doctrine at
  // block level. Per-cell guards mirror the pre-grouping conditionals.
  const settlementCells = [
    calculatedValues.contractValueSettlement !== undefined
      ? {
          key: 'cv-settle',
          label: `Contract Value (${effectiveSettlementCurrency})`,
          value: money(calculatedValues.contractValueSettlement, effectiveSettlementCurrency),
        }
      : null,
    calculatedValues.expectedDailyReturnSettlement !== undefined
      ? {
          key: 'edr-settle',
          label: `Expected Daily Return (${effectiveSettlementCurrency})`,
          value: money(
            calculatedValues.expectedDailyReturnSettlement,
            effectiveSettlementCurrency,
          ),
          valueClassName: signColor(calculatedValues.expectedDailyReturnSettlement),
        }
      : null,
    calculatedValues.marginPriceSettlement !== undefined
      ? {
          key: 'mp-settle',
          label: `Margin Price (${effectiveSettlementCurrency})`,
          value: money(calculatedValues.marginPriceSettlement, effectiveSettlementCurrency),
        }
      : null,
    calculatedValues.netSettlementConverted !== undefined
      ? {
          key: 'ns-settle',
          label: `Net Settlement (${effectiveSettlementCurrency})`,
          value: money(calculatedValues.netSettlementConverted, effectiveSettlementCurrency),
          valueClassName: 'text-blue-600',
        }
      : null,
    showClosingSettlement && calculatedValues.closingSettlementConverted !== undefined
      ? {
          key: 'cs-settle',
          label: `Closing Settlement (${effectiveSettlementCurrency})`,
          value: money(calculatedValues.closingSettlementConverted, effectiveSettlementCurrency),
          valueClassName: 'text-purple-600',
        }
      : null,
  ].filter((cell): cell is SummaryCell => cell !== null);

  const neutralCells = [dailyRateCell, marginCell, ...(fxRateCell ? [fxRateCell] : [])];
  // Shared metrics keep the SAME order as the settlement block so the two
  // blocks align column-by-column for visual comparison; the block-specific
  // Collateral Required trails at the end.
  const securityCells = [
    contractValueSecurity,
    expectedDailyReturnSecurity,
    marginPriceSecurity,
    netSettlementSecurity,
    ...(closingSettlementSecurity ? [closingSettlementSecurity] : []),
    collateralRequired,
  ];

  return (
    <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
      {neutralCells.map((cell) => (
        <MetricCell key={cell.key} cell={cell} />
      ))}
      {settlementCells.length > 0 && (
        <>
          <CurrencyBlockHeader currency={effectiveSettlementCurrency} role="Settlement" />
          {settlementCells.map((cell) => (
            <MetricCell key={cell.key} cell={cell} />
          ))}
        </>
      )}
      <CurrencyBlockHeader currency={securityCurrency} role="Security" />
      {securityCells.map((cell) => (
        <MetricCell key={cell.key} cell={cell} />
      ))}
    </div>
  );
};
